import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Loader2, HeartHandshake } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import PtfButton from "@/components/PtfButton";
import { Reveal } from "@/components/motion/Reveal";
import { api, formatApiError } from "@/lib/api";
import { IMAGES } from "@/lib/content";
import { openCheckout } from "@/lib/payments";

const PRESETS = [500, 1000, 2500, 5000];
const inputCls = "w-full bg-transparent border-b border-ptf-primary/25 py-3 text-base outline-none transition-colors duration-300 focus:border-ptf-secondary placeholder:text-ptf-text/40 text-ptf-text";

const USES = [
  { title: "Conservation on the ground", text: "Restoration, habitat protection and the field teams who carry it out." },
  { title: "Community programmes", text: "Livelihoods, education, health and water work in the villages we serve." },
  { title: "Honest documentation", text: "Measuring outcomes and publishing what works, and what does not." },
];

function DonationForm() {
  const { data: config } = useQuery({ queryKey: ["payments-config"], queryFn: async () => (await api.get("/payments/config")).data });
  const [amount, setAmount] = useState(1000);
  const [custom, setCustom] = useState("");
  const [frequency, setFrequency] = useState("one-time");
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const finalAmount = custom ? Number(custom) : amount;

  const submit = async (e) => {
    e.preventDefault();
    if (!finalAmount || finalAmount < 1) { toast.error("Please choose a donation amount."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/donations", { ...form, amount: finalAmount, frequency });
      if (!data.payment) {
        setDone({ paid: false, message: data.message });
        return;
      }
      const resp = await openCheckout({
        keyId: data.key_id, orderId: data.order_id, amount: data.amount, currency: data.currency,
        description: "Donation to Planet Trotter Foundation",
        prefill: { name: form.name, email: form.email, contact: form.phone },
        tracking: { content_name: "Donation", content_type: "donation", product_type: "donation" },
      });
      const v = await api.post("/donations/verify", {
        razorpay_order_id: resp.razorpay_order_id,
        razorpay_payment_id: resp.razorpay_payment_id,
        razorpay_signature: resp.razorpay_signature,
        fbp: resp.fbp, fbc: resp.fbc,
      });
      setDone({ paid: true, message: v.data.message });
    } catch (err) {
      toast.error(err?.response ? formatApiError(err.response.data?.detail) : err.message || "Could not process the donation.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <motion.div data-testid="donation-success" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="py-6">
        <span className="grid place-items-center h-14 w-14 rounded-full bg-ptf-accent text-ptf-primary mb-6"><Check className="h-7 w-7" /></span>
        <h3 className="font-display uppercase text-3xl tracking-tight text-ptf-primary">{done.paid ? "Thank you." : "Noted, with gratitude."}</h3>
        <p className="mt-4 max-w-md text-ptf-text/75 leading-relaxed">{done.message}</p>
        <button type="button" onClick={() => setDone(null)} className="mt-8 text-xs uppercase tracking-[0.18em] underline underline-offset-4 text-ptf-primary hover:text-ptf-secondary">
          Make another donation
        </button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="donation-form" className="space-y-8">
      {config && !config.enabled && (
        <p data-testid="payments-pending-note" className="border border-ptf-primary/15 bg-ptf-primary/[0.04] px-4 py-3 text-sm text-ptf-text/70 leading-relaxed">
          Online payments are opening soon. You can record your donation intent below and we will write to you the moment they are live.
        </p>
      )}

      <div>
        <p className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-text/60 mb-3">Frequency</p>
        <div className="flex gap-2">
          {[{ k: "one-time", label: "One-time" }, { k: "monthly", label: "Monthly" }].map(({ k, label }) => (
            <button
              key={k}
              type="button"
              data-testid={`freq-${k}`}
              onClick={() => setFrequency(k)}
              className={`px-5 py-2.5 text-[0.7rem] font-semibold uppercase tracking-[0.14em] border transition-colors ${frequency === k ? "bg-ptf-primary text-ptf-bg border-ptf-primary" : "border-ptf-primary/25 text-ptf-text/70 hover:border-ptf-primary"}`}
            >
              {label}
            </button>
          ))}
        </div>
        <AnimatePresence>
          {frequency === "monthly" && (
            <motion.p initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} data-testid="monthly-note" className="mt-3 text-xs text-ptf-text/55 leading-relaxed">
              We record your monthly pledge and our team writes to you to set it up. No money moves until you confirm.
            </motion.p>
          )}
        </AnimatePresence>
      </div>

      <div>
        <p className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-text/60 mb-3">Amount (INR)</p>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button
              key={p}
              type="button"
              data-testid={`amount-${p}`}
              onClick={() => { setAmount(p); setCustom(""); }}
              className={`px-5 py-2.5 text-sm font-medium border transition-colors ${!custom && amount === p ? "bg-ptf-primary text-ptf-bg border-ptf-primary" : "border-ptf-primary/25 text-ptf-text/75 hover:border-ptf-primary"}`}
            >
              ₹{p.toLocaleString("en-IN")}
            </button>
          ))}
          <input
            type="number"
            min="1"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            placeholder="Custom ₹"
            data-testid="amount-custom"
            className={`w-32 px-4 py-2.5 text-sm border outline-none transition-colors ${custom ? "border-ptf-primary bg-ptf-primary/5" : "border-ptf-primary/25"} focus:border-ptf-secondary`}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
        <input required data-testid="dn-name" value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name *" />
        <input required type="email" data-testid="dn-email" value={form.email} onChange={set("email")} className={inputCls} placeholder="Email *" />
        <input data-testid="dn-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="Phone" />
        <input data-testid="dn-message" value={form.message} onChange={set("message")} className={inputCls} placeholder="A note, if you like" />
      </div>

      <div>
        <PtfButton type="submit" disabled={loading} data-testid="dn-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {loading ? "Processing" : `Donate ₹${(finalAmount || 0).toLocaleString("en-IN")}`}
        </PtfButton>
        <p className="mt-4 text-[0.65rem] text-ptf-text/50 leading-relaxed max-w-md">
          Every donation is acknowledged by email, and how funds are used is
          published on our Transparency page.
        </p>
      </div>
    </form>
  );
}

export default function Donate() {
  return (
    <>
      <Seo title="Donate" path="/donate" description="Support Planet Trotter Foundation's conservation, responsible tourism and community development work across India and beyond." />
      <PageHero
        eyebrow="Donate"
        titleLines={["Fund the quiet,", "patient work."]}
        intro="Conservation and community development move at the speed of trust. Your support keeps that work going between the milestones."
        image={IMAGES.forest}
      />

      <section className="bg-ptf-bg py-24 md:py-32">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-4">
            <p className="text-[0.7rem] uppercase tracking-[0.28em] text-ptf-primary/60 mb-6 flex items-center gap-3">
              <span className="h-px w-8 bg-ptf-primary/40 inline-block" /> Where it goes
            </p>
            <ul className="space-y-7">
              {USES.map((u) => (
                <li key={u.title} className="flex items-start gap-4">
                  <span className="shrink-0 grid place-items-center h-10 w-10 rounded-full bg-ptf-primary/5"><HeartHandshake className="h-5 w-5 text-ptf-secondary" /></span>
                  <div>
                    <h3 className="font-display uppercase text-xl tracking-tight text-ptf-primary">{u.title}</h3>
                    <p className="mt-1.5 text-sm text-ptf-text/70 leading-relaxed">{u.text}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-8">
            <Reveal>
              <div className="bg-white border border-ptf-primary/10 p-7 md:p-10">
                <DonationForm />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
